import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  ThemeProvider,
  createTheme,
  Autocomplete,
  Box,
  Chip,
  Typography,
  Switch as MuiSwitch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Divider,
} from "@mui/material";
import { Edit, Trash2, Info, ChevronDown } from "lucide-react";
import { apiClientDrawerV2 as apiClient } from "@lift-admin/utils";
import {
  Button,
  DataTable,
  Badge,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Input,
  Label,
  Switch,
  SpinnerLoader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Toastify } from 'lift-ui';
import BrandMultiSelect from "./brand-multiselect";
import OrganizationMultiSelect from "./organization-multiselect";
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import {
  buildAccessLogChanges,
  buildAccessLogDetails,
  inferNextAccessModeFromSignedIds,
} from "@/features/drawer-structure-v2/drawer-parts/accessLogHelpers";
import {
  buildDisplayPositionChange,
  buildDisplayPositionDetail,
} from "@/features/drawer-structure-v2/drawer-parts/displayPositionLogHelpers";
import iconOptions from "../../../constants/iconOptions.json";

const theme = createTheme({
  palette: {
    primary: {
      main: "#16306D",
    },
    secondary: {
      main: "rgba(0, 0, 0, 0)",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

interface ItemsProps {
  searchText: string;
  toggle: string;
  channels: any[];
  applications?: any[];
  openDialog: boolean;
  setOpenDialog: any;
  brands: any[];
  organizations: any[];
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

interface FormStateEdit {
  name: string;
  icon: string;
  path: string;
  channel_id: number | null;
  brands_exclude: number[];
  organizations_exclude: number[];
  display_order: number;
}

interface FormStateAdd {
  name: string;
  icon: string;
  path: string;
  channel_id: number | null;
  brands_exclude: number[];
  organizations_exclude: number[];
  display_order: number;
}

// Helper function to render brand badges
const renderBrandBadges = (data: any) => {
  if (
    !data ||
    !data.access?.brand_level ||
    !data.access.brand_level.brands ||
    !Array.isArray(data.access.brand_level.brands) ||
    data.access.brand_level.brands.length === 0
  ) {
    return <span className="text-gray-400">--</span>;
  }

  const brandIds = data.access.brand_level.brands;
  const access_type = data.access.brand_level.access_mode;

  const brandNames = brandIds.map((brand: any) => brand.name);

  return (
    <div className="flex flex-wrap gap-1 max-h-16 overflow-auto">
      {brandNames.map((name: string, index: number) => (
        <Badge
          key={index}
          variant={access_type === "EXCLUDE" ? "destructive" : "default"}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

// Helper function to render organization badges
const renderOrganizationBadges = (data: any) => {
  if (
    !data ||
    !data.access?.organization_level ||
    !data.access.organization_level.organizations ||
    !Array.isArray(data.access.organization_level.organizations) ||
    data.access.organization_level.organizations.length === 0
  ) {
    return <span className="text-gray-400">--</span>;
  }

  const organizationIds = data.access.organization_level.organizations;
  const access_type = data.access.organization_level.access_mode;

  const organizationNames = organizationIds.map((org: any) => org.name);

  return (
    <div className="flex flex-wrap gap-1 max-h-16 overflow-auto">
      {organizationNames.map((name: string, index: number) => (
        <Badge
          key={index}
          variant={access_type === "EXCLUDE" ? "destructive" : "default"}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const Groupings = ({
  toggle,
  channels,
  applications = [],
  searchText,
  openDialog,
  setOpenDialog,
  brands,
  organizations,
  hasEditAccess = true,
  hasManageAccess = true,
}: ItemsProps) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerGroupings, setDrawerGroupings] = useState<any>([]);
  const [drawerGroupingsBackUp, setDrawerGroupingBackup] = useState<any>([]);
  const [channelsWithChildren, setChannelsWithChildren] = useState<any>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [currentIdData, setCurrentIdData] = useState<any>(null);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [pathError, setPathError] = useState("");
  const [searchedResults, setSearchedResults] = useState<any>([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [formStateEdit, setFormStateEdit] = useState<FormStateEdit>({
    name: "",
    icon: "",
    path: "",
    channel_id: null,
    brands_exclude: [],
    organizations_exclude: [],
    display_order: 0,
  });
  const [formStateAdd, setFormStateAdd] = useState<FormStateAdd>({
    name: "",
    icon: "",
    path: "",
    channel_id: null,
    brands_exclude: [],
    organizations_exclude: [],
    display_order: 0,
  });

  const [makeItTopAdd, setMakeItTopAdd] = useState<boolean>(false);
  const [makeItBelowAdd, setMakeItBelowAdd] = useState<string>("");
  const [makeItTopEdit, setMakeItTopEdit] = useState<boolean>(false);
  const [makeItBelowEdit, setMakeItBelowEdit] = useState<string>("");

  const getChannelsWithChildren = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await apiClient.get('/admin/channels', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      const channelsData = response.data.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      console.log("channelsData", channelsData);
      setChannelsWithChildren(channelsData);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    }
  };


  const getDrawrGroupings = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') as any);
      const res = await apiClient.get("/admin/groupings", {
        params: {
          include_inactive: true,
          user_id: user?.id,
          is_admin: true,
          is_team: false
        }
      });

      const grouping = (res?.data || []).sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );

      if (searchText.trim().length === 0) {
        setDrawerGroupings(grouping);
      } else {
        setDrawerGroupings(
          grouping.filter((item: any) => searchedResults.includes(item.id))
        );
      }

      setDrawerGroupingBackup(grouping);
    } catch {
      Toastify("error", "Verticals not fetched");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    if (toggle === "groupings") {
      getChannelsWithChildren();
      getDrawrGroupings();
    }
  }, [toggle]);

  const channelMapping = useMemo(() => channels?.reduce((acc: Record<string, string>, ch: any) => {
    acc[ch.id] = ch.name;
    return acc;
  }, {}) ?? {}, [channels]);

  const getApplicationName = (channelId: string | number | null | undefined) => {
    if (channelId == null) return '--';
    const channel = (channels || []).find((c: any) => String(c.id) === String(channelId));
    if (!channel?.application_id) return '--';
    const app = (applications || []).find((a: any) => String(a.id) === String(channel.application_id));
    return (app as any)?.display_name || app?.name || '--';
  };

  const channelLabelWithParent = (channel: any) => {
    const name = channel?.name || String(channel?.id ?? '');
    const appName = getApplicationName(channel?.id);
    return appName && appName !== '--' ? `${name} (${appName})` : name;
  };

  // Pagination calculations
  const paginatedData = useMemo(() => {
    return drawerGroupings.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [drawerGroupings, page, rowsPerPage]);

  const totalPages = Math.ceil(drawerGroupings.length / rowsPerPage);

  // Get available groupings for "Make it Below" dropdown in Add dialog
  const availableGroupingsForBelowAdd = useMemo(() => {
    if (!formStateAdd.channel_id) return [];

    const selectedChannel = channelsWithChildren.find((channel: any) => channel.id === formStateAdd.channel_id);
    if (!selectedChannel || !selectedChannel.children) return [];

    return selectedChannel.children
  }, [channelsWithChildren, formStateAdd.channel_id]);

  // Get available groupings for "Make it Below" dropdown in Edit dialog
  const availableGroupingsForBelowEdit = useMemo(() => {
    if (!formStateEdit.channel_id || !currentIdData?.id) return [];

    const selectedChannel = channelsWithChildren.find((channel: any) => channel.id === formStateEdit.channel_id);
    if (!selectedChannel || !selectedChannel.children) return [];

    return selectedChannel.children
      .filter((grouping: any) => grouping.id !== currentIdData?.id)
  }, [channelsWithChildren, formStateEdit.channel_id, currentIdData?.id]);


  const handleAddChannel = async () => {
    const kebabCaseRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;

    let valid = true;

    if (formStateAdd.path && !kebabCaseRegex.test(formStateAdd.path)) {
      setPathError("Path should be in kebab-case");
      valid = false;
    } else {
      setPathError("");
    }

    if (!formStateAdd.name || formStateAdd.name.length == 0) {
      Toastify("error", "Please enter correct name");
      return;
    }

    // Validate display order selection
    if (!makeItTopAdd && !makeItBelowAdd) {
      Toastify("error", "Please either select 'Make it Top' or choose a grouping in 'Make it Below'");
      return;
    }

    if (!valid) return;

    try {
      setLoadData(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Calculate new display_order based on user selection
      let newDisplayOrder = 0;

      if (makeItTopAdd) {
        newDisplayOrder = 0;
      } else if (makeItBelowAdd && availableGroupingsForBelowAdd.length > 0) {
        const selectedGrouping = availableGroupingsForBelowAdd.find((grouping: any) => grouping.id === makeItBelowAdd);
        if (selectedGrouping) {
          newDisplayOrder = (selectedGrouping.display_order || 0) + 1;
        }
      }

      const payload = {
        name: formStateAdd.name,
        icon: formStateAdd.icon,
        path: formStateAdd.path,
        channel_id: formStateAdd.channel_id,
        display_order: newDisplayOrder,
        below_item: makeItTopAdd ? null : makeItBelowAdd,
      };

      const res = await apiClient.post('/admin/groupings', payload, {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      const newGroupingId = res.data?.id || res.data?.grouping?.id;

      // Handle brand access if any brands are selected
      if (formStateAdd.brands_exclude.length > 0) {
        const excludedBrands = formStateAdd.brands_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedBrands = formStateAdd.brands_exclude.filter((id: number) => id > 0);

        let accessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let brandIds: string[] = [];

        if (excludedBrands.length > 0) {
          accessMode = "EXCLUDE";
          brandIds = excludedBrands.map((id: number) => id.toString());
        } else if (includedBrands.length > 0) {
          accessMode = "INCLUDE";
          brandIds = includedBrands.map((id: number) => id.toString());
        }

        if (brandIds.length > 0) {
          let brandAccessData: any[] = [];

          for (const brandId of brandIds) {
            let data = {
              brand_id: brandId,
              level_type: "grouping",
              level_ids: [newGroupingId],
              access_mode: accessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }

          try {
            let payload = {
              data: brandAccessData
            }
            await apiClient.post(`/admin/brand-access?user_id=${user.id}`, payload);
          } catch (brandAccessError) {
            console.error("Error creating brand access:", brandAccessError);
            Toastify("warning", "Brand access creation failed");
          }
        }
      }

      // Handle organization access if any organizations are selected
      if (formStateAdd.organizations_exclude.length > 0) {
        const excludedOrganizations = formStateAdd.organizations_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedOrganizations = formStateAdd.organizations_exclude.filter((id: number) => id > 0);

        let accessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let organizationIds: string[] = [];

        if (excludedOrganizations.length > 0) {
          accessMode = "EXCLUDE";
          organizationIds = excludedOrganizations.map((id: number) => id.toString());
        } else if (includedOrganizations.length > 0) {
          accessMode = "INCLUDE";
          organizationIds = includedOrganizations.map((id: number) => id.toString());
        }

        if (organizationIds.length > 0) {
          let organizationAccessData: any[] = [];

          for (const organizationId of organizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "grouping",
              level_ids: [newGroupingId],
              access_mode: accessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }

          try {
            let payload = {
              data: organizationAccessData
            }
            await apiClient.post(`/admin/organization-access?user_id=${user.id}`, payload);
          } catch (organizationAccessError) {
            console.error("Error creating organization access:", organizationAccessError);
            Toastify("warning", "Organization access creation failed");
          }
        }
      }

      const brandsExcludeForLog = [...(formStateAdd.brands_exclude || [])];
      const orgsExcludeForLog = [...(formStateAdd.organizations_exclude || [])];
      const groupingFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        { key: "icon", label: "Icon" },
        { key: "path", label: "Path" },
        {
          key: "channel_id",
          label: "Channel",
          resolve: (id) =>
            channelMapping?.[id] || (id ? String(id) : "Not Set"),
        },
      ];
      const normalizedGrouping = {
        name: payload.name,
        icon: payload.icon,
        path: payload.path,
        channel_id: payload.channel_id,
      };
      const desc = {
        message: buildSummary("Grouping", payload?.name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_grouping",
        grouping_id: newGroupingId || null,
        grouping_name: payload?.name,
        user_id: user?.id,
        user_name: user?.name,
        details: [
          ...buildDetails(normalizedGrouping, groupingFieldSpecs, {
            skipEmpty: true,
          }),
          buildDisplayPositionDetail({
            makeItTop: makeItTopAdd,
            belowItemId: makeItBelowAdd,
            peers: availableGroupingsForBelowAdd,
            displayOrder: newDisplayOrder,
            peerLabel: "Grouping",
          }),
          ...buildAccessLogDetails(
            "Brand",
            brandsExcludeForLog,
            brands || [],
            inferNextAccessModeFromSignedIds(brandsExcludeForLog, "EXCLUDE")
          ),
          ...buildAccessLogDetails(
            "Organization",
            orgsExcludeForLog,
            organizations || [],
            inferNextAccessModeFromSignedIds(orgsExcludeForLog, "EXCLUDE")
          ),
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      getDrawrGroupings();
      getChannelsWithChildren();
      setFormStateAdd({
        name: "",
        icon: "",
        path: "",
        channel_id: null,
        brands_exclude: [],
        organizations_exclude: [],
        display_order: 0,
      });
      setSelectedChannel(null);
      setMakeItTopAdd(false);
      setMakeItBelowAdd("");
      setOpenDialog(false);
    } catch (err: any) {
      setOpenDialog(false);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (err.response?.data?.detail) {
        const errorMessages = Array.isArray(err.response.data.detail) ? err.response.data.detail.map((error: any) => `${error.loc?.join(".")}: ${error.msg}`).join(", ") : err.response.data.detail;
        Toastify("error", errorMessages);
      } else {
        Toastify("error", "Grouping not added");
      }
      const errorDesc = {
        message: `Failed to create Grouping (Grouping Name: "${formStateAdd?.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/admin/groupings",
        grouping_name: formStateAdd?.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  const handleDelete = async (id: any) => {
    console.log(id);
    try {
      setLoadData(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Find the grouping data to get access information before deletion
      const groupingToDelete = drawerGroupings.find((grouping: any) => grouping.id === id);

      // Remove brand access if exists
      if (groupingToDelete?.access?.brand_level?.brands?.length > 0) {
        const brands = groupingToDelete.access.brand_level.brands;
        const accessMode = groupingToDelete.access.brand_level.access_mode;

        let brandAccessData: any[] = [];

        for (const brand of brands) {
          let data = {
            brand_id: brand.id.toString(),
            level_type: "grouping",
            level_ids: [id],
            access_mode: accessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "remove"
          };
          brandAccessData.push(data);
        }

        if (brandAccessData.length > 0) {
          try {
            let payload = {
              data: brandAccessData
            }
            await apiClient.post(`/admin/brand-access?user_id=${user.id}`, payload);
          } catch (brandAccessError) {
            console.error("Error removing brand access:", brandAccessError);
            Toastify("warning", "Brand access removal failed");
          }
        }
      }

      // Remove organization access if exists
      if (groupingToDelete?.access?.organization_level?.organizations?.length > 0) {
        const organizations = groupingToDelete.access.organization_level.organizations;
        const accessMode = groupingToDelete.access.organization_level.access_mode;

        let organizationAccessData: any[] = [];

        for (const organization of organizations) {
          let data = {
            organization_id: organization.id.toString(),
            level_type: "grouping",
            level_ids: [id],
            access_mode: accessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "remove"
          };
          organizationAccessData.push(data);
        }

        if (organizationAccessData.length > 0) {
          try {
            let payload = {
              data: organizationAccessData
            }
            await apiClient.post(`/admin/organization-access?user_id=${user.id}`, payload);
          } catch (organizationAccessError) {
            console.error("Error removing organization access:", organizationAccessError);
            Toastify("warning", "Organization access removal failed");
          }
        }
      }

      // Delete the grouping itself
      const res = await apiClient.delete(`/admin/groupings/${id}`, {
        params: {
          user_id: user?.id,
          grouping_id: id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      getDrawrGroupings();
      getChannelsWithChildren();
      setOpenDeleteDialog(false);
      Toastify("success", "Item Deleted successfully");

      const desc = {
        message: buildSummary(
          "Grouping",
          groupingToDelete?.name,
          "deleted"
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_grouping",
        grouping_id: id,
        grouping_name: groupingToDelete?.name,
        user_id: user?.id,
        user_name: user?.name,
        details: [
          { label: "Grouping ID", value: String(id ?? "Not Set") },
          {
            label: "Channel",
            value:
              channelMapping?.[groupingToDelete?.channel_id] ||
              String(groupingToDelete?.channel_id ?? "Not Set"),
          },
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify("error", "Grouping not deleted");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const groupingToDelete = drawerGroupings.find(
        (grouping: any) => grouping.id === id
      );
      const errorDesc = {
        message: `Failed to delete Grouping (Grouping ID: ${id}, Grouping Name: "${groupingToDelete?.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err?.response?.status,
        method: "DELETE",
        url: `/admin/groupings/${id}`,
        grouping_id: id,
        grouping_name: groupingToDelete?.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  const handleEdit = async () => {
    console.log(formStateEdit);
    if (!currentIdData?.id) return;

    // Validate display order selection
    if (!makeItTopEdit && !makeItBelowEdit) {
      Toastify("error", "Please either select 'Make it Top' or choose a grouping in 'Make it Below'");
      return;
    }

    console.log(formStateEdit);
    try {
      setLoadData(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Calculate new display_order based on user selection
      let newDisplayOrder = formStateEdit.display_order;

      if (makeItTopEdit) {
        newDisplayOrder = 0;
      } else if (makeItBelowEdit && availableGroupingsForBelowEdit.length > 0) {
        const selectedGrouping = availableGroupingsForBelowEdit.find((grouping: any) => grouping.id === makeItBelowEdit);
        if (selectedGrouping) {
          newDisplayOrder = (selectedGrouping.display_order || 0) + 1;
        }
      }

      const payload = {
        ...formStateEdit,
        id: currentIdData?.id,
        display_order: newDisplayOrder,
        below_item: makeItTopEdit ? null : makeItBelowEdit,
      };
      const res = await apiClient.post('/admin/groupings', payload, {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      // Check if brand_exclude is changed
      const originalBrands = (currentIdData as any)?.access?.brand_level?.brands || [];
      const originalBrandsExclude = originalBrands.map((brand: any) => {
        const accessMode = (currentIdData as any)?.access?.brand_level?.access_mode;
        return accessMode === "EXCLUDE" ? -parseInt(brand.id) : parseInt(brand.id);
      });

      const brandChanged = JSON.stringify(originalBrandsExclude.sort()) !== JSON.stringify(formStateEdit.brands_exclude.sort());

      if (brandChanged) {
        // Determine original access mode and brand IDs
        const originalAccessMode = (currentIdData as any)?.access?.brand_level?.access_mode || "INCLUDE";
        const originalBrandIds = originalBrandsExclude.map((id: number) => Math.abs(id).toString());

        // Determine new access mode and brand IDs from formStateEdit.brands_exclude
        const excludedBrands = formStateEdit.brands_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedBrands = formStateEdit.brands_exclude.filter((id: number) => id > 0);

        let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let newBrandIds: string[] = [];

        if (excludedBrands.length > 0) {
          newAccessMode = "EXCLUDE";
          newBrandIds = excludedBrands.map((id: number) => id.toString());
        } else if (includedBrands.length > 0) {
          newAccessMode = "INCLUDE";
          newBrandIds = includedBrands.map((id: number) => id.toString());
        }

        // Call brand-access API for each brand
        let brandAccessData: any[] = [];

        // Case 1: Access mode changed - need to remove all old entries and add all new entries
        if (originalAccessMode !== newAccessMode) {
          // Remove all brands from the old access mode
          for (const brandId of originalBrandIds) {
            let data = {
              brand_id: brandId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: originalAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            brandAccessData.push(data);
          }

          // Add all brands to the new access mode
          for (const brandId of newBrandIds) {
            let data = {
              brand_id: brandId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }
        } else {
          // Case 2: Same access mode - handle additions and removals
          const addedBrands = newBrandIds.filter((id: string) => !originalBrandIds.includes(id));
          const removedBrands = originalBrandIds.filter((id: string) => !newBrandIds.includes(id));

          // Add entries for added brands
          for (const brandId of addedBrands) {
            let data = {
              brand_id: brandId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }

          // Add entries for removed brands
          for (const brandId of removedBrands) {
            let data = {
              brand_id: brandId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            brandAccessData.push(data);
          }
        }

        if (brandAccessData.length > 0) {
          try {
            console.log('brandAccessData', brandAccessData);
            let payload = {
              data: brandAccessData
            }
            await apiClient.post(`/admin/brand-access?user_id=${user.id}`, payload);
          } catch (brandAccessError) {
            console.error("Error updating brand access:", brandAccessError);
            Toastify("warning", "Brand access update failed");
          }
        }
      }

      // Check if organizations_exclude is changed
      const originalOrganizations = (currentIdData as any)?.access?.organization_level?.organizations || [];
      const originalOrganizationsExclude = originalOrganizations.map((org: any) => {
        const accessMode = (currentIdData as any)?.access?.organization_level?.access_mode;
        return accessMode === "EXCLUDE" ? -parseInt(org.id) : parseInt(org.id);
      });

      const organizationChanged = JSON.stringify(originalOrganizationsExclude.sort()) !== JSON.stringify(formStateEdit.organizations_exclude.sort());

      if (organizationChanged) {
        // Determine original access mode and organization IDs
        const originalAccessMode = (currentIdData as any)?.access?.organization_level?.access_mode || "INCLUDE";
        const originalOrganizationIds = originalOrganizationsExclude.map((id: number) => Math.abs(id).toString());

        // Determine new access mode and organization IDs from formStateEdit.organizations_exclude
        const excludedOrganizations = formStateEdit.organizations_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedOrganizations = formStateEdit.organizations_exclude.filter((id: number) => id > 0);

        let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let newOrganizationIds: string[] = [];

        if (excludedOrganizations.length > 0) {
          newAccessMode = "EXCLUDE";
          newOrganizationIds = excludedOrganizations.map((id: number) => id.toString());
        } else if (includedOrganizations.length > 0) {
          newAccessMode = "INCLUDE";
          newOrganizationIds = includedOrganizations.map((id: number) => id.toString());
        }

        // Call organization-access API for each organization
        let organizationAccessData: any[] = [];

        // Case 1: Access mode changed - need to remove all old entries and add all new entries
        if (originalAccessMode !== newAccessMode) {
          // Remove all organizations from the old access mode
          for (const organizationId of originalOrganizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: originalAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            organizationAccessData.push(data);
          }

          // Add all organizations to the new access mode
          for (const organizationId of newOrganizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }
        } else {
          // Case 2: Same access mode - handle additions and removals
          const addedOrganizations = newOrganizationIds.filter((id: string) => !originalOrganizationIds.includes(id));
          const removedOrganizations = originalOrganizationIds.filter((id: string) => !newOrganizationIds.includes(id));

          // Add entries for added organizations
          for (const organizationId of addedOrganizations) {
            let data = {
              organization_id: organizationId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }

          // Add entries for removed organizations
          for (const organizationId of removedOrganizations) {
            let data = {
              organization_id: organizationId,
              level_type: "grouping",
              level_ids: [currentIdData?.id],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            organizationAccessData.push(data);
          }
        }

        if (organizationAccessData.length > 0) {
          try {
            console.log('organizationAccessData', organizationAccessData);
            let payload = {
              data: organizationAccessData
            }
            await apiClient.post(`/admin/organization-access?user_id=${user.id}`, payload);
          } catch (organizationAccessError) {
            console.error("Error updating organization access:", organizationAccessError);
            Toastify("warning", "Organization access update failed");
          }
        }
      }

      Toastify("success", "Item updated successfully");
      const nextBrandsExclude = [...(formStateEdit.brands_exclude || [])];
      const nextOrgsExclude = [...(formStateEdit.organizations_exclude || [])];
      const groupingFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        { key: "icon", label: "Icon" },
        { key: "path", label: "Path" },
        {
          key: "channel_id",
          label: "Channel",
          resolve: (id) =>
            channelMapping?.[id] || (id ? String(id) : "Not Set"),
        },
      ];
      const previousGrouping = {
        name: currentIdData?.name,
        icon: currentIdData?.icon,
        path: currentIdData?.path,
        channel_id: currentIdData?.channel_id,
      };
      const nextGrouping = {
        name: formStateEdit?.name,
        icon: formStateEdit?.icon,
        path: formStateEdit?.path,
        channel_id: formStateEdit?.channel_id,
      };
      const changes = diffFields(
        previousGrouping,
        nextGrouping,
        groupingFieldSpecs
      );

      const selectedChannelForLog = channelsWithChildren.find(
        (channel: any) => channel.id === formStateEdit?.channel_id
      );
      const groupingPeers =
        selectedChannelForLog?.children || availableGroupingsForBelowEdit;
      const positionChange = buildDisplayPositionChange({
        previousOrder: Number(currentIdData?.display_order) || 0,
        nextOrder: newDisplayOrder ?? 0,
        nextMakeItTop: makeItTopEdit,
        nextBelowItemId: makeItBelowEdit,
        peers: groupingPeers,
        currentId: currentIdData?.id,
        peerLabel: "Grouping",
      });
      if (positionChange) changes.push(positionChange);

      const brandLookup = [...(brands || []), ...(originalBrands || [])];
      const orgLookup = [
        ...(organizations || []),
        ...(originalOrganizations || []),
      ];
      changes.push(
        ...buildAccessLogChanges(
          "Brand",
          originalBrandsExclude,
          nextBrandsExclude,
          brandLookup,
          {
            previousMode: (currentIdData as any)?.access?.brand_level
              ?.access_mode,
            nextMode: inferNextAccessModeFromSignedIds(
              nextBrandsExclude,
              (currentIdData as any)?.access?.brand_level?.access_mode
            ),
          }
        )
      );
      changes.push(
        ...buildAccessLogChanges(
          "Organization",
          originalOrganizationsExclude,
          nextOrgsExclude,
          orgLookup,
          {
            previousMode: (currentIdData as any)?.access?.organization_level
              ?.access_mode,
            nextMode: inferNextAccessModeFromSignedIds(
              nextOrgsExclude,
              (currentIdData as any)?.access?.organization_level?.access_mode
            ),
          }
        )
      );
      const desc = {
        message: buildSummary(
          "Grouping",
          formStateEdit?.name || currentIdData?.name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_grouping",
        grouping_id: currentIdData?.id,
        grouping_name: formStateEdit?.name || currentIdData?.name,
        user_id: user?.id,
        user_name: user?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      getDrawrGroupings();
      getChannelsWithChildren();
      handleEditDialogClose();
    } catch (err: any) {
      if (err.response?.data?.detail) {
        const errorMessages = Array.isArray(err.response.data.detail) ? err.response.data.detail.map((error: any) => `${error.loc?.join(".")}: ${error.msg}`).join(", ") : err.response.data.detail;
        Toastify("error", errorMessages);
      } else {
        Toastify("error", "Item not updated");
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: `Failed to update Grouping (Grouping ID: ${currentIdData?.id}, Grouping Name: "${formStateEdit?.name || currentIdData?.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/admin/groupings",
        grouping_id: currentIdData?.id,
        grouping_name: formStateEdit?.name || currentIdData?.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  const handleEditOpen = (data: any) => {
    setCurrentIdData(data);
    console.log('data', data, channelsWithChildren, channels);

    let brands_exclude: number[] = [];
    let organizations_exclude: number[] = [];

    // Handle brand level access
    if (data.access?.brand_level) {
      const brandLevel = data.access.brand_level;
      if (brandLevel.brands && Array.isArray(brandLevel.brands)) {
        if (brandLevel.access_mode === "EXCLUDE") {
          // For exclude mode, store as negative IDs
          brands_exclude = brandLevel.brands.map((brand: any) => -parseInt(brand.id));
        } else if (brandLevel.access_mode === "INCLUDE") {
          // For include mode, store as positive IDs
          brands_exclude = brandLevel.brands.map((brand: any) => parseInt(brand.id));
        }
      }
    }

    // Handle organization level access
    if (data.access?.organization_level) {
      const orgLevel = data.access.organization_level;
      if (orgLevel.organizations && Array.isArray(orgLevel.organizations)) {
        if (orgLevel.access_mode === "EXCLUDE") {
          // For exclude mode, store as negative IDs
          organizations_exclude = orgLevel.organizations.map((org: any) => -parseInt(org.id));
        } else if (orgLevel.access_mode === "INCLUDE") {
          // For include mode, store as positive IDs
          organizations_exclude = orgLevel.organizations.map((org: any) => parseInt(org.id));
        }
      }
    }

    setFormStateEdit({
      name: data?.name,
      icon: data?.icon,
      path: data?.path,
      channel_id: data?.channel_id,
      brands_exclude,
      organizations_exclude,
      display_order: data?.display_order || 0,
    });

    // Set initial display order state
    setMakeItTopEdit(data?.display_order === 0);

    // If display_order is not 0, find the grouping that's currently above this one
    let initialMakeItBelowEdit = "";
    if (data?.display_order !== 0) {
      const selectedChannel = channelsWithChildren.find((channel: any) => channel.id === data?.channel_id);
      console.log('selectedChannel', selectedChannel, data?.channel_id, channelsWithChildren);
      if (selectedChannel && selectedChannel.children) {
        // Get all groupings including current one, sorted by display_order
        const allGroupingsInChannel = [...selectedChannel.children]

        // Find current grouping's position in sorted list
        const currentGroupingIndex = allGroupingsInChannel.findIndex((grouping: any) => grouping.id === data?.id);

        // If current grouping is not at position 0, get the grouping above it
        if (currentGroupingIndex > 0) {
          const groupingAbove = allGroupingsInChannel[currentGroupingIndex - 1];
          initialMakeItBelowEdit = groupingAbove.id;
        }
      }
    }

    console.log('Edit Open - Display Order:', data?.display_order, 'Selected Below:', initialMakeItBelowEdit);

    // Use setTimeout to ensure the state updates after the form state is set
    setTimeout(() => {
      setMakeItBelowEdit(initialMakeItBelowEdit);
    }, 0);

    const selectedChannelObj = channels.find(
      (channel) => channel.id === data?.channel_id
    );
    setSelectedChannel(selectedChannelObj);
    setOpenEditDialog(true);
  };

  const handleSubmit = () => {
    handleAddChannel();
  };

  const handleEditDialogClose = () => {
    setFormStateEdit({
      name: "",
      icon: "",
      path: "",
      channel_id: null,
      brands_exclude: [],
      organizations_exclude: [],
      display_order: 0,
    });
    setCurrentIdData(null);
    setMakeItTopEdit(false);
    setMakeItBelowEdit("");
    setOpenEditDialog(false);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFormStateAdd({
      name: "",
      icon: "",
      path: "",
      channel_id: null,
      brands_exclude: [],
      organizations_exclude: [],
      display_order: 0,
    });
    setSelectedChannel(null);
    setMakeItTopAdd(false);
    setMakeItBelowAdd("");
  };

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerGroupingsBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (
        filteredUsers[i]["name"]?.toLowerCase().includes(query.toLowerCase()) ||
        filteredUsers[i]["path"]?.toLowerCase().includes(query.toLowerCase())
      )
        results.push(filteredUsers[i]);
    }

    setSearchedResults(results.map((item: any) => item.id));
    setDrawerGroupings(results);
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText]);

  // Reset "Make it Below" selection when channel changes in Add dialog
  useEffect(() => {
    setMakeItBelowAdd("");
  }, [formStateAdd.channel_id]);

  // Reset "Make it Below" selection when channel changes in Edit dialog
  useEffect(() => {
    setMakeItBelowEdit("");
  }, [formStateEdit.channel_id]);

  // Reset "Make it Below" when "Make it Top" is enabled in Add dialog
  useEffect(() => {
    if (makeItTopAdd) {
      setMakeItBelowAdd("");
    }
  }, [makeItTopAdd]);

  // Reset "Make it Below" when "Make it Top" is enabled in Edit dialog
  useEffect(() => {
    if (makeItTopEdit) {
      setMakeItBelowEdit("");
    }
  }, [makeItTopEdit]);

  // Automatically select "Make it Top" when channel has no other groupings in Add dialog
  useEffect(() => {
    if (availableGroupingsForBelowAdd.length === 0 && !makeItTopAdd) {
      setMakeItTopAdd(true);
      setMakeItBelowAdd("");
    }
  }, [availableGroupingsForBelowAdd.length, makeItTopAdd]);

  // Automatically select "Make it Top" when channel has no other groupings in Edit dialog
  useEffect(() => {
    if (availableGroupingsForBelowEdit.length === 0 && !makeItTopEdit) {
      setMakeItTopEdit(true);
      setMakeItBelowEdit("");
    }
  }, [availableGroupingsForBelowEdit.length, makeItTopEdit]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <div className="flex flex-col h-full">
          <p className="text-sm text-muted-foreground mb-3">
            Different reports and items are grouped under these parent names.
          </p>
          <div className="flex-1 min-h-0 flex flex-col">
            <DataTable
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="100%"
              columns={(() => {
                const cols: TableColumn<any>[] = [
                  { key: "application", header: "Application", accessor: (row: any) => getApplicationName(row.channel_id), width: "160px", render: (_v: any, row: any) => <span className="text-gray-600">{getApplicationName(row.channel_id)}</span> },
                  { key: "channel", header: "Channel", accessor: (row: any) => channelMapping[row.channel_id] || "--", width: "160px", render: (_v: any, row: any) => <span className="text-gray-600">{channelMapping[row.channel_id] || "--"}</span> },
                  { key: "name", header: "Name", accessor: "name", width: "180px", render: (_v: any, row: any) => <span className="font-medium">{row.name}</span> },
                  { key: "path", header: (<span className="inline-flex items-center gap-1">Path <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" /></TooltipTrigger><TooltipContent><p>This is the exact name you&apos;ll see in the URL.</p></TooltipContent></Tooltip></TooltipProvider></span>), accessor: "path", width: "200px", render: (_v: any, row: any) => <span className="text-gray-600">{row.path}</span> },
                  { key: "brands", header: "Excluded/Include Brands", accessor: (row: any) => row.id, width: "200px", render: (_v: any, row: any) => renderBrandBadges(row) },
                  { key: "organizations", header: "Excluded/Include Organizations", accessor: (row: any) => row.id, width: "200px", render: (_v: any, row: any) => renderOrganizationBadges(row) },
                  { key: "display_order", header: "Order", accessor: "display_order", width: "80px", align: "center" },
                  { key: "created_at", header: "Created", accessor: (row: any) => formatDate(row.created_at), width: "120px", render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.created_at)}</span> },
                  { key: "updated_at", header: "Updated", accessor: (row: any) => formatDate(row.updated_at), width: "120px", render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.updated_at)}</span> },
                ];
                if (showActionsColumn) {
                  cols.push({
                    key: "actions",
                    header: "Actions",
                    accessor: (row: any) => row.id,
                    width: "120px",
                    align: "center",
                    render: (_v: any, row: any) => (
                      <div className="flex gap-1 justify-center">
                        {hasEditAccess && (
                          <Button variant="ghost" size="sm" onClick={() => handleEditOpen(row)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {hasManageAccess && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setOpenDeleteDialog(true);
                              setCurrentIdData(row.id);
                            }}
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ),
                  });
                }
                return cols;
              })()}
              data={paginatedData}
              loading={loadData}
              emptyMessage="No groupings found."
              stickyHeader
              showActions={false}
              pagination={{
                currentPage: page + 1,
                totalPages: Math.max(1, totalPages),
                pageSize: rowsPerPage,
                totalItems: drawerGroupings.length,
                onPageChange: (p) => setPage(p - 1),
                onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
                pageSizeOptions: [10, 20, 50, 100],
              }}
            />
          </div>
        </div>
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Grouping</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this grouping?
            </Typography>
            {currentIdData && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  <strong>Name:</strong> {drawerGroupings.find((g: any) => g.id === currentIdData)?.name || 'Unknown'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Icon:</strong> {drawerGroupings.find((g: any) => g.id === currentIdData)?.icon || 'No icon'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Path:</strong> {drawerGroupings.find((g: any) => g.id === currentIdData)?.path || 'No path'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Channel:</strong> {channels.find((c: any) => c.id === drawerGroupings.find((g: any) => g.id === currentIdData)?.channel_id)?.name || 'No channel'}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)} color="primary" variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={loadData}
              onClick={() => {
                handleDelete(currentIdData);
              }}
              color="error"
              variant="contained"
            >
              {loadData ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        <Sheet open={openDialog} onOpenChange={(open) => !open && handleDialogClose()}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-2xl flex flex-col h-full"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Add New Grouping</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="add-name">Name</Label>
                <Input
                  id="add-name"
                  value={formStateAdd.name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setFormStateAdd((prev) => ({
                      ...prev,
                      name: newName,
                      path: newName.toLowerCase().replace(/\s+/g, "-"),
                    }));
                  }}
                  placeholder="Enter name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-icon">Icon</Label>
                <Select
                  value={formStateAdd.icon || undefined}
                  onValueChange={(value) =>
                    setFormStateAdd((prev) => ({
                      ...prev,
                      icon: value || "",
                    }))
                  }
                >
                  <SelectTrigger id="add-icon" className="w-full">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((option: any) => (
                      <SelectItem key={option.link} value={option.link}>
                        <div className="flex items-center gap-2">
                          {option.link && (
                            <img src={option.link} alt={option.icon} className="w-5 h-5" />
                          )}
                          <span>{option.icon}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-path">Path</Label>
                <Input
                  id="add-path"
                  disabled
                  value={formStateAdd.path}
                  className={pathError ? "border-red-500" : ""}
                />
                {pathError && (
                  <p className="text-sm text-red-500">{pathError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-channel">Select Channel Name</Label>
                <Select
                  value={formStateAdd.channel_id?.toString() || undefined}
                  onValueChange={(value) => {
                    const channel = channels.find((c: any) => c.id.toString() === value);
                    setSelectedChannel(channel || null);
                    setFormStateAdd((prev) => ({
                      ...prev,
                      channel_id: channel?.id || null,
                    }));
                  }}
                >
                  <SelectTrigger id="add-channel" className="w-full">
                    <SelectValue placeholder="Select a channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id.toString()}>
                        {channelLabelWithParent(channel)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold">Display Order</h3>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    Control the position of this grouping within the selected channel. Enable "Make it Top" to move to position top of the channel, or select another grouping to position below. Order is 0 based indexing.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={makeItTopAdd}
                    onCheckedChange={setMakeItTopAdd}
                    disabled={availableGroupingsForBelowAdd.length === 0}
                  />
                  <Label htmlFor="make-it-top-add">Move to Top</Label>
                </div>

                {makeItTopAdd && availableGroupingsForBelowAdd.length === 0 && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>No other groupings are present in this channel.</p>
                  </div>
                )}

                {!makeItTopAdd && (
                  <div className="space-y-2">
                    <Label htmlFor="make-it-below-add">Make it Below *</Label>
                    <Select
                      value={makeItBelowAdd || undefined}
                      onValueChange={(value) => setMakeItBelowAdd(value)}
                      disabled={availableGroupingsForBelowAdd.length === 0}
                    >
                      <SelectTrigger id="make-it-below-add" className={`w-full ${!makeItBelowAdd ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Select a grouping" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableGroupingsForBelowAdd.length === 0 ? (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                            No other groupings available in this channel
                          </div>
                        ) : (
                          availableGroupingsForBelowAdd.map((grouping: any) => (
                            <SelectItem key={grouping.id} value={grouping.id}>
                              {grouping.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="text-sm text-muted-foreground">
                  {(() => {
                    if (makeItTopAdd) {
                      return <><strong>Current Position:</strong> <span>Top (Order: 0)</span></>;
                    } else if (makeItBelowAdd) {
                      const selectedGroupingIndex = availableGroupingsForBelowAdd.findIndex((grouping: any) => grouping.id === makeItBelowAdd);
                      const selectedGrouping = availableGroupingsForBelowAdd.find((grouping: any) => grouping.id === makeItBelowAdd);
                      const groupingName = selectedGrouping ? selectedGrouping.name : makeItBelowAdd;
                      const newOrder = selectedGroupingIndex !== -1 ? selectedGroupingIndex + 1 : 0;
                      return <><strong>Current Position:</strong> <span>Below "{groupingName}" (Order: {newOrder})</span></>;
                    } else {
                      return <><strong>Current Position:</strong> <span>Not Set</span></>;
                    }
                  })()}
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Manage Access</h3>

                <Collapsible className="mb-6">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Brand Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific brands. Use positive IDs (e.g., 1, 2) to include only those specific brands.
                    </p>
                    <BrandMultiSelect
                      defaultBrands={formStateAdd.brands_exclude}
                      setDefaultBrands={(field: string, value: number[]) => {
                        setFormStateAdd((prev) => ({
                          ...prev,
                          brands_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible className="mb-8">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Organization Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific organizations. Use positive IDs (e.g., 1, 2) to include only those specific organizations.
                    </p>
                    <OrganizationMultiSelect
                      defaultOrganizations={formStateAdd.organizations_exclude}
                      setDefaultOrganizations={(field: string, value: number[]) => {
                        setFormStateAdd((prev) => ({
                          ...prev,
                          organizations_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
            </div>
            <SheetFooter className="gap-2 border-t pt-4 flex-shrink-0 mt-auto">
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  loadData ||
                  !formStateAdd.name.trim() ||
                  !formStateAdd.channel_id ||
                  (!makeItTopAdd && !makeItBelowAdd)
                }
              >
                {loadData ? (
                  <>
                    <SpinnerLoader className="mr-2 h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
        <Sheet open={openEditDialog} onOpenChange={(open) => {
          if (!open) {
            handleEditDialogClose();
          } else {
            setOpenEditDialog(true);
          }
        }}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-2xl flex flex-col h-full"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Edit Grouping</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
              value={formStateEdit.name}
              onChange={(e) => {
                const newName = e.target.value;
                setFormStateEdit((prev) => ({
                  ...prev,
                  name: newName,
                  path: newName.toLowerCase().replace(/\s+/g, "-"),
                }));
              }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-icon">Icon</Label>
                <Select
                  value={formStateEdit.icon || undefined}
                  onValueChange={(value) =>
                setFormStateEdit((prev) => ({
                  ...prev,
                      icon: value || "",
                    }))
                  }
                >
                  <SelectTrigger id="edit-icon" className="w-full">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((option: any) => (
                      <SelectItem key={option.link} value={option.link}>
                        <div className="flex items-center gap-2">
                  {option.link && (
                            <img src={option.link} alt={option.icon} className="w-5 h-5" />
                          )}
                          <span>{option.icon}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-path">Path</Label>
                <Input
                  id="edit-path"
              disabled
              value={formStateEdit.path}
                  className={pathError ? "border-red-500" : ""}
            />
                {pathError && (
                  <p className="text-sm text-red-500">{pathError}</p>
                )}
              </div>

            {/* <TextField
              label="Display Order"
              type="number"
              value={formStateEdit.display_order}
              onChange={(e) =>
                setFormStateEdit((prev) => ({
                  ...prev,
                  display_order: parseInt(e.target.value) || 0,
                }))
              }
              fullWidth
              style={{ marginBottom: "16px" }}
              inputProps={{ min: 0 }}
            /> */}

              <div className="space-y-2">
                <Label htmlFor="edit-channel">Select Channel Name</Label>
                <Select
                  value={formStateEdit.channel_id?.toString() || undefined}
                  onValueChange={(value) => {
                    const channel = channels.find((c: any) => c.id.toString() === value);
                    setSelectedChannel(channel || null);
                setFormStateEdit((prev) => ({
                  ...prev,
                      channel_id: channel?.id || null,
                }));
              }}
                >
                  <SelectTrigger id="edit-channel" className="w-full">
                    <SelectValue placeholder="Select a channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id.toString()}>
                        {channelLabelWithParent(channel)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold">Display Order</h3>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
              Control the position of this grouping within the selected channel. Enable "Make it Top" to move to position top of the channel, or select another grouping to position below. Order is 0 based indexing.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                <Switch
                  checked={makeItTopEdit}
                    onCheckedChange={setMakeItTopEdit}
              disabled={availableGroupingsForBelowEdit.length === 0}
            />
                  <Label htmlFor="make-it-top-edit">Move to Top</Label>
                </div>

            {makeItTopEdit && availableGroupingsForBelowEdit.length === 0 && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>No other groupings are present in this channel.</p>
                  </div>
            )}

            {!makeItTopEdit && (
                  <div className="space-y-2">
                    <Label htmlFor="make-it-below-edit">Make it Below *</Label>
                <Select
                      value={makeItBelowEdit || undefined}
                      onValueChange={(value) => setMakeItBelowEdit(value)}
                  disabled={availableGroupingsForBelowEdit.length === 0}
                >
                      <SelectTrigger id="make-it-below-edit" className={`w-full ${!makeItBelowEdit ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Select a grouping" />
                      </SelectTrigger>
                      <SelectContent>
                  {availableGroupingsForBelowEdit.length === 0 ? (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No other groupings available in this channel
                          </div>
                  ) : (
                          availableGroupingsForBelowEdit.map((grouping: any) => (
                            <SelectItem key={grouping.id} value={grouping.id}>
                        {grouping.name}
                            </SelectItem>
                    ))
                  )}
                      </SelectContent>
                </Select>
                  </div>
            )}

            {/* Show Current Position */}
                <div className="text-sm text-muted-foreground">
              {(() => {
                if (makeItTopEdit) {
                      return <><strong>Current Position:</strong> <span>Top (Order: 0)</span></>;
                } else if (makeItBelowEdit) {
                  const selectedGroupingIndex = availableGroupingsForBelowEdit.findIndex((grouping: any) => grouping.id === makeItBelowEdit);
                  const selectedGrouping = availableGroupingsForBelowEdit.find((grouping: any) => grouping.id === makeItBelowEdit);
                  const groupingName = selectedGrouping ? selectedGrouping.name : makeItBelowEdit;
                  const newOrder = selectedGroupingIndex !== -1 ? selectedGroupingIndex + 1 : formStateEdit.display_order;
                      return <><strong>Current Position:</strong> <span>Below "{groupingName}" (Order: {newOrder})</span></>;
                } else {
                      return <><strong>Current Position:</strong> <span>Not Set</span></>;
                }
              })()}
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Manage Access</h3>

                <Collapsible className="mb-6">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Brand Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific brands. Use positive IDs (e.g., 1, 2) to include only those specific brands.
                    </p>
                    <BrandMultiSelect
                      defaultBrands={formStateEdit.brands_exclude}
                      setDefaultBrands={(field: string, value: number[]) => {
                        setFormStateEdit((prev) => ({
                          ...prev,
                          brands_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible className="mb-8">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Organization Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific organizations. Use positive IDs (e.g., 1, 2) to include only those specific organizations.
                    </p>
                    <OrganizationMultiSelect
                      defaultOrganizations={formStateEdit.organizations_exclude}
                      setDefaultOrganizations={(field: string, value: number[]) => {
                        setFormStateEdit((prev) => ({
                          ...prev,
                          organizations_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
            </div>
            <SheetFooter className="gap-2 border-t pt-4 flex-shrink-0 mt-auto">
              <Button variant="outline" onClick={handleEditDialogClose}>
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={
                loadData ||
                !formStateEdit.name.trim() ||
                !formStateEdit.channel_id ||
                (!makeItTopEdit && !makeItBelowEdit)
              }
            >
                {loadData ? (
                  <>
                    <SpinnerLoader className="mr-2 h-4 w-4" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </ThemeProvider>
    </>
  );
};

export default Groupings;
